import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, XCircle } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { clear } = useCart();
  const [state, setState] = useState("polling"); // polling | error | expired
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      if (attempts >= MAX_ATTEMPTS) {
        setState("error");
        return;
      }
      attempts += 1;
      try {
        const res = await getCheckoutStatus(sessionId);
        if (res.payment_status === "paid" && res.order_id) {
          clear();
          navigate(`/order/${res.order_id}`, { replace: true });
          return;
        }
        if (res.status === "expired") {
          setState("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [navigate, clear]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-bone pt-[72px] flex flex-col items-center justify-center gap-5 px-5 text-center">
      {state === "polling" ? (
        <>
          <div className="w-16 h-16 rounded-full bg-forest flex items-center justify-center animate-pulse">
            <Sparkles className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
          <p className="font-display text-3xl text-ink">Confirming your payment…</p>
          <p className="text-stone max-w-sm">Hang tight — we're finalizing your order. This only takes a moment.</p>
        </>
      ) : (
        <>
          <div className="w-16 h-16 rounded-full bg-sand flex items-center justify-center">
            <XCircle className="w-8 h-8 text-stone" strokeWidth={1.5} />
          </div>
          <p className="font-display text-3xl text-ink">
            {state === "expired" ? "Your checkout session expired." : "We couldn't confirm your payment."}
          </p>
          <p className="text-stone max-w-sm">If you were charged, your order will still appear in your account shortly.</p>
          <div className="flex gap-3 mt-2">
            <button onClick={() => navigate("/checkout")} className="rounded-full bg-forest text-[#14100F] px-6 py-3 text-sm font-semibold hover:bg-forest-dark transition-colors">
              Try again
            </button>
            <button onClick={() => navigate("/account")} className="rounded-full border border-line px-6 py-3 text-sm font-semibold text-ink hover:bg-sand transition-colors">
              View account
            </button>
          </div>
        </>
      )}
    </div>
  );
}
